import { create } from 'zustand';
import type { ActivityDefinition, ActivityLog } from '../types/activityTracking';
import {
  getActivityDefinitions,
  seedDefaultActivities,
  addActivityDefinition,
  updateActivityDefinition,
  deleteActivityDefinition,
  getActivityLog,
  toggleActivityCompletion,
  setSponsorSharing,
  getCheckinCompletionsForWeek,
} from '../services/firebase/activityTracking';

/** Get the ISO date string for a given Date */
function toISODate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

/**
 * Compute the 7 ISO date strings for the week containing `today`,
 * starting on `weekStartDay` (0=Sunday … 6=Saturday).
 */
export function getWeekDates(today: Date, weekStartDay: number): string[] {
  const day = today.getDay();
  const diff = (day - weekStartDay + 7) % 7;
  const startDate = new Date(today);
  startDate.setDate(today.getDate() - diff);
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(startDate);
    d.setDate(startDate.getDate() + i);
    return toISODate(d);
  });
}

interface ActivityState {
  definitions: ActivityDefinition[];
  currentLog: ActivityLog | null;
  weekOffset: number;
  weekStartDay: number;
  isLoading: boolean;
  weekDates: string[];

  initialize: (userId: string, weekStartDay: number) => Promise<void>;
  navigateWeek: (userId: string, offset: number) => Promise<void>;
  toggleCompletion: (userId: string, activityId: string, dateStr: string, completed: boolean) => Promise<void>;
  addDefinition: (userId: string, def: Omit<ActivityDefinition, 'id' | 'createdAt' | 'updatedAt'>) => Promise<void>;
  updateDefinition: (userId: string, defId: string, updates: Partial<ActivityDefinition>) => Promise<void>;
  removeDefinition: (userId: string, defId: string) => Promise<void>;
  setSharing: (userId: string, weekStart: string, shared: boolean) => Promise<void>;
  reset: () => void;
}

export const useActivityStore = create<ActivityState>((set, get) => ({
  definitions: [],
  currentLog: null,
  weekOffset: 0,
  weekStartDay: 0,
  isLoading: false,
  weekDates: [],

  initialize: async (userId, weekStartDay) => {
    set({ isLoading: true, weekStartDay });

    let defs = await getActivityDefinitions(userId);
    if (defs.length === 0) {
      defs = await seedDefaultActivities(userId);
    }

    const weekDates = getWeekDates(new Date(), weekStartDay);
    const weekStart = weekDates[0];

    let log = await getActivityLog(userId, weekStart);
    if (!log) {
      log = {
        weekStart,
        completions: {},
        sharedWithSponsor: false,
        sponsorComment: '',
        sponsorCommentAt: null,
        updatedAt: null as unknown as import('firebase/firestore').Timestamp,
      };
    }

    log = await mergeAutoTracked(userId, log, defs, weekDates);

    set({ definitions: defs, currentLog: log, weekDates, weekOffset: 0, isLoading: false });
  },

  navigateWeek: async (userId, offset) => {
    const { weekStartDay, definitions } = get();
    set({ isLoading: true, weekOffset: offset });

    const baseDate = new Date();
    baseDate.setDate(baseDate.getDate() + offset * 7);
    const weekDates = getWeekDates(baseDate, weekStartDay);
    const weekStart = weekDates[0];

    let log = await getActivityLog(userId, weekStart);
    if (!log) {
      log = {
        weekStart,
        completions: {},
        sharedWithSponsor: false,
        sponsorComment: '',
        sponsorCommentAt: null,
        updatedAt: null as unknown as import('firebase/firestore').Timestamp,
      };
    }

    log = await mergeAutoTracked(userId, log, definitions, weekDates);
    set({ currentLog: log, weekDates, isLoading: false });
  },

  toggleCompletion: async (userId, activityId, dateStr, completed) => {
    const { currentLog, weekDates } = get();
    if (!currentLog) return;

    // Optimistic update
    const currentDates = currentLog.completions[activityId] ?? [];
    const newDates = completed
      ? [...new Set([...currentDates, dateStr])]
      : currentDates.filter(d => d !== dateStr);

    set({
      currentLog: {
        ...currentLog,
        completions: { ...currentLog.completions, [activityId]: newDates },
      },
    });

    await toggleActivityCompletion(userId, weekDates[0], activityId, dateStr, completed);
  },

  addDefinition: async (userId, def) => {
    const added = await addActivityDefinition(userId, def);
    set(state => ({ definitions: [...state.definitions, added] }));
  },

  updateDefinition: async (userId, defId, updates) => {
    await updateActivityDefinition(userId, defId, updates);
    set(state => ({
      definitions: state.definitions.map(d => d.id === defId ? { ...d, ...updates } : d),
    }));
  },

  removeDefinition: async (userId, defId) => {
    await deleteActivityDefinition(userId, defId);
    set(state => ({ definitions: state.definitions.filter(d => d.id !== defId) }));
  },

  setSharing: async (userId, weekStart, shared) => {
    await setSponsorSharing(userId, weekStart, shared);
    set(state => ({
      currentLog: state.currentLog
        ? { ...state.currentLog, sharedWithSponsor: shared }
        : null,
    }));
  },

  reset: () => set({ definitions: [], currentLog: null, weekOffset: 0, isLoading: false }),
}));

async function mergeAutoTracked(
  userId: string,
  log: ActivityLog,
  defs: ActivityDefinition[],
  weekDates: string[]
): Promise<ActivityLog> {
  const autoTracked = defs.filter(d => d.isAutoTracked && d.isActive);
  if (autoTracked.length === 0) return log;

  const checkins = await getCheckinCompletionsForWeek(userId, weekDates);
  const mergedCompletions = { ...log.completions };

  for (const def of autoTracked) {
    if (def.autoTrackSource === 'morning_checkin') {
      mergedCompletions[def.id] = checkins.morning;
    } else if (def.autoTrackSource === 'nightly_review') {
      mergedCompletions[def.id] = checkins.nightly;
    }
  }

  return { ...log, completions: mergedCompletions };
}
